

import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";
import CreateLedger from "../../TypesAndGroups/Ladgers/CreateLadger";
import Pagination, { usePagination } from "../../../Components/CommonComponent/Pgination/pagination";
import AddButton from "../../../Components/CommonComponent/Button/AddButton";
import DeleteDataModal from "../../../Components/CommonComponent/Modals/DeleteDataModal";
import { CommonContext } from "../../../App";
import { useTranslation } from "react-i18next";
import api from "../../../services/api";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";

export default function ItemsTable() {
  const { t } = useTranslation();
  // State management
  const [ledgers, setLedgers] = useState({ allData: [], pagination: { totalRecords: 0 } });
  const [loading, setLoading] = useState(false);
  const { branchId } = useContext(CommonContext)
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const [loadinData, setLoadinData] = useState(false)
  const [searchTerm, setSearchTerm] = useState("");
  const [checkSearchTerm, setCheckSearchTerm] = useState(false);


  // Pagination hook
  const {
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    resetPagination,
    getPaginatedData
  } = usePagination(10);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    return getPaginatedData(ledgers);
  }, [ledgers, getPaginatedData]);

  // Memoized serial number calculation
  const getSerialNumber = useCallback((index) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  }, [currentPage, itemsPerPage]);

  // Fetch ledger data - memoized to prevent unnecessary re-renders
  const fetchLedgerData = useCallback(async (search = "") => {
    try {
      setLoading(true);
      const response = await api.get(`ledgers/get-all-ledger?page=${currentPage}&limit=${itemsPerPage}&branchId=${branchId}&type=Stock&search=${encodeURIComponent(search)}`);

      // Expecting API to return either { success, data: [], totalRecords } or { success, allData: [], pagination: { totalRecords } }
      if (response.data?.success) {
        const payload = response.data;
        const allData = Array.isArray(payload?.data)
          ? payload.data
          : (Array.isArray(payload?.allData) ? payload.allData : []);
        const totalRecords = payload?.totalRecords ?? payload?.pagination?.totalRecords ?? allData.length;
        setLedgers({ allData, pagination: { totalRecords } });
      } else {
        toast.error(response.data?.message || "Failed to fetch ledgers");
        setLedgers({ allData: [], pagination: { totalRecords: 0 } });
      }
    } catch (error) {
      console.error("Error fetching ledgers:", error);
      toast.error(error.response?.data?.message || "Error fetching ledgers");
      setLedgers({ allData: [], pagination: { totalRecords: 0 } });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, branchId]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (searchTerm.length == 0) {
      setCheckSearchTerm(false);
    }
  };
  useEffect(() => {
    if (checkSearchTerm && searchTerm.trim() === "") {
      // handlePageChange(1);      
      fetchLedgerData("");
    }
  }, [searchTerm]);


  // Effect to fetch data on component mount
  useEffect(() => {
    fetchLedgerData();
  }, [fetchLedgerData]);

  // Memoized handlers to prevent unnecessary re-renders



  // const handleAddGroup = useCallback(() => {
  //   setSelectedLedger(null);
  //   setModalVisible(true);
  // }, []);
 const handleAddLedger = useCallback(() => {
    const trimmedValue = searchTerm.trim();
    if (trimmedValue.length > 0) {
      handlePageChange(1);
      fetchLedgerData(trimmedValue);
      setCheckSearchTerm(true);
    } else {
      setSelectedLedger(null);
      setModalVisible(true);
      setCheckSearchTerm(false);
    }
  }, [searchTerm, fetchLedgerData, handlePageChange]);
  const handleEditLedger = useCallback((ledger) => {
    setSelectedLedger(ledger);
    setModalVisible(true);
  }, []);

  const handleDeleteLedger = useCallback((ledger) => {
    setSelectedLedger(ledger);
    setModalDelete(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setSelectedLedger(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setModalDelete(false);
    setSelectedLedger(null);
  }, []);

  const handleLedgerUpdate = useCallback(() => {
    fetchLedgerData();
    setSelectedLedger(null);
    handleModalClose();
  }, [fetchLedgerData]);



  // Memoized table rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    return paginationData.currentItems?.map((ledger, index) => (
      <tr key={ledger.id || ledger.groupId || index} className="bg-theme-tableBg hover:bg-theme-tableHoverColor ">
        <td className="border-b py-[2px]  px-1 text-center">
          {getSerialNumber(index)}
        </td>
        <td className="border-b py-[2px]  px-1">
          {ledger.name || "-"}{language !== "en" && (ledger.cousinName && ` (${ledger.cousinName})`)}
        </td>
        <td className="border-b py-[2px] px-1">
          {ledger.groupType}/{ledger.groupName}
          {ledger.subGroupName && `/${ledger.subGroupName}`}
        </td>
        <td className="border-b py-[2px]  px-1">
          <div className="flex justify-center gap-2">
            <FaEdit
              title="Edit ledger"
              onClick={ledger.branchId !== 0 ? () => handleEditLedger(ledger) : undefined}
              className={
                ledger.branchId !== 0
                  ? "text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                  : "text-gray-400 mt-1 opacity-50 pointer-events-none"
              }
            />
            <RiDeleteBin6Fill
              title="Delete ledger"
              onClick={ledger.branchId !== 0 ? () => handleDeleteLedger(ledger) : undefined}
              className={
                ledger.branchId !== 0
                  ? "text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                  : "text-red-400 mt-1 opacity-50 pointer-events-none"
              }
            />
          </div>
        </td>
      </tr>
    ));
  }, [paginationData.currentItems, getSerialNumber, handleEditLedger, handleDeleteLedger]);

  return (
    <div className="py-2 flex justify-center">
      <div className="flex flex-col justify-center  w-full">
        {/* Header */}
        <div className="flex justify-between py-[2px] items-center  bg-theme-headerBg px-2 text-theme-color">
          <div className="  font-medium">{t('items')} </div>
          {/* <div className="">
            <AddButton onClick={handleAddGroup} text={t("add")} />
          </div> */}
            <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search group..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="px-2 py-[2px] text-sm rounded-1 border border-gray-300 focus:outline-none text-black outline-none"
                      />
          
                      {/* <AddButton onClick={handleAddGroup} text={t("add")} /> */}
                      <AddButton
                        onClick={handleAddLedger}
                        text={searchTerm.trim() ? "Search" : t("add")}
                      />
                    </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="flex justify-center my-4">
              <LoadingSpinner size={70} color='#be8024' />
            </div>
          </div>
        )}

        {/* Ledger List */}
        {!loading && (ledgers?.pagination?.totalRecords ?? 0) === 0 ? (
          <p className="text-gray-500 italic text-center py-8">
            {t('noDataAvailable')}
          </p>
        ) : !loading && (
          <div className="overflow-x-auto">
            <table className="w-full rounded-lg text-sm  border-separate border-spacing-0 ">
              <thead>
                <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                  <th className="border-b py-[2px] font-medium  px-1 text-center">{t('srNumber')}</th>
                  <th className="border-b py-[2px] font-medium  px-1 text-left">{t('name')} </th>
                  <th className="border-b py-[2px] font-medium  px-1 text-left">{t('type&Group')} </th>
                  <th className="border-b py-[2px] font-medium  px-1 text-center">{t('actions')} </th>
                </tr>
              </thead>
              <tbody>
                {tableRows}
              </tbody>
            </table>

            {/* Pagination Component */}
            <Pagination
              data={ledgers}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 50, 100]}
              showItemsPerPageSelector={true}
              showPageInfo={true}
              position="bottom-center"
            />
          </div>
        )}
      </div>

      {/* Edit Ledger Modal */}
      {modalVisible && (
        <div
          className="fixed inset-0 bg-black/20 bg-opacity-40 flex justify-center items-center z-[1100]"
        // onClick={handleModalClose}
        >
          <div
            className="rounded max-w-5xl text-start max-h-[70vh] w-full shadow-lg overflow-y-auto custom-scrollbar "
          // onClick={(e) => e.stopPropagation()}
          >
            <CreateLedger
              setModalVisible={(val) => {
                if (val === false) handleModalClose();
              }}
              onUpdateLedger={handleLedgerUpdate}
              ledgerData={selectedLedger}
              updateLedger={fetchLedgerData}
              isEdit={!!selectedLedger}
              manageStock={true}
              addMoreBtn={true}
            />
          </div>
        </div>
      )}
      {modalDelete && (
        <DeleteDataModal
          selectedLedger={selectedLedger}
          handleDeleteModalClose={handleDeleteModalClose}
          handleDeleteLedgerApi={async (ledger) => {
            try {
              const res = await api.post(`ledgers/delete-ledger?id=${ledger.id}`, {
                isDelete: 1, branchId // soft delete
              });

              if (res.data.success) {
                toast.success(res.data.message || "Ledger deleted successfully");

                // ✅ Re-fetch ledgers to update table
                fetchLedgerData();

                // Optional: Adjust current page if last item on page was deleted
                const totalRecordsAfterDelete = (ledgers?.pagination?.totalRecords ?? 1) - 1;
                const totalPagesAfterDelete = Math.ceil(totalRecordsAfterDelete / itemsPerPage);
                if (currentPage > totalPagesAfterDelete && totalPagesAfterDelete > 0) {
                  handlePageChange(totalPagesAfterDelete);
                }
              } else {
                toast.error(res.data.message || "Failed to delete ledger");
              }
            } catch (err) {
              console.error("Error deleting ledger:", err);
              toast.error(err?.response?.data?.message || "Server error");
            } finally {
              handleDeleteModalClose();
            }
          }}
        />
      )}

    </div>
  );
}
